import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Check, Flag, Gauge, MapPin, Upload } from 'lucide-react';
import CourseSearchPanel from '../components/CourseSearchPanel';
import { GOLF_CLUBS } from '../constants/golfClubs';
import { distanceUnitLabel, toCanonicalDistanceYards } from '../constants/units';
import { useDeviceCourses } from '../context/DeviceCoursesContext';
import { useProfile } from '../context/ProfileContext';
import { swingApi } from '../api/swingApi';
import { ClubSwingAverage, UpsertClubInput } from '../types';

type SetupStep = 'courses' | 'clubs' | 'ready';

const emptyClub = (): UpsertClubInput => ({
  clubType: '',
  clubBrand: '',
  clubModel: '',
  carryDistance: 0,
  totalDistance: 0,
  ballSpeed: 0,
  launchAngle: 0,
  launchDirection: 0,
  apex: 0,
  sideCarry: 0,
  clubSpeed: 0,
  smashFactor: 0,
  descentAngle: 0,
  attackAngle: 0,
  clubPath: 0,
});

const SetupPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const { profile } = useProfile();
  const { savedCourses, hydrated: coursesHydrated } = useDeviceCourses();
  const [averages, setAverages] = useState<ClubSwingAverage[]>([]);
  const [step, setStep] = useState<SetupStep | null>(null);
  const [loadingClubs, setLoadingClubs] = useState(true);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [club, setClub] = useState<UpsertClubInput>(emptyClub);
  const measurementSystem = profile.measurementSystem;
  const distanceUnit = distanceUnitLabel(measurementSystem);

  useEffect(() => {
    let cancelled = false;
    swingApi.getAverages(profile.userId)
      .then((data) => {
        if (!cancelled) setAverages(data);
      })
      .catch(() => {
        toast.current?.show({ severity: 'error', summary: 'Club data unavailable', detail: 'We could not check your club data. Please try again.', life: 4000 });
      })
      .finally(() => {
        if (!cancelled) setLoadingClubs(false);
      });
    return () => { cancelled = true; };
  }, [profile.userId]);

  useEffect(() => {
    if (step !== null || !coursesHydrated || loadingClubs) return;
    setStep(savedCourses.length === 0 ? 'courses' : averages.length === 0 ? 'clubs' : 'ready');
  }, [averages.length, coursesHydrated, loadingClubs, savedCourses.length, step]);

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const result = await swingApi.import(file, profile.userId, true);
      const updated = await swingApi.getAverages(profile.userId);
      setAverages(updated);
      toast.current?.show({ severity: 'success', summary: 'Club data imported', detail: `${result.imported} shots are ready for your caddie.`, life: 3500 });
      if (result.warnings.length > 0) {
        toast.current?.show({ severity: 'warn', summary: 'Some columns were not recognized', detail: result.warnings.join(' '), life: 8000, sticky: true });
      }
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Import failed', detail: 'Choose a supported launch-monitor CSV or Excel file and try again.', life: 4000 });
    } finally {
      setImporting(false);
    }
  };

  const handleAddClub = async () => {
    if (!club.clubType || club.carryDistance <= 0) {
      toast.current?.show({ severity: 'warn', summary: 'Add the essentials', detail: 'Choose a club and enter its carry distance.', life: 3500 });
      return;
    }
    setSaving(true);
    try {
      const carryDistance = toCanonicalDistanceYards(club.carryDistance, measurementSystem);
      await swingApi.upsertClub({
        ...club,
        clubBrand: club.clubBrand?.trim() ?? '',
        clubModel: club.clubModel?.trim() ?? '',
        carryDistance,
        totalDistance: toCanonicalDistanceYards(club.totalDistance || club.carryDistance, measurementSystem),
      }, profile.userId);
      const updated = await swingApi.getAverages(profile.userId);
      setAverages(updated);
      setClub(emptyClub());
      toast.current?.show({ severity: 'success', summary: 'Club added', detail: 'Add another club or continue when your bag is ready.', life: 3500 });
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Save failed', detail: 'We could not save that club. Please try again.', life: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const steps: Array<{ id: SetupStep; label: string }> = [
    { id: 'courses', label: 'Courses' },
    { id: 'clubs', label: 'Your bag' },
    { id: 'ready', label: 'Ready' },
  ];
  const activeIndex = steps.findIndex((item) => item.id === step);

  if (step === null) {
    return <div className="setup-loading"><i className="pi pi-spin pi-spinner" aria-label="Checking your setup" /></div>;
  }

  return (
    <div className="setup-page">
      <Toast ref={toast} />
      <header className="setup-topbar">
        <img src={`${process.env.PUBLIC_URL}/wholelogo-navy.svg`} alt="CaddieSight" />
        <span>Complete your setup</span>
      </header>

      <nav className="setup-progress" aria-label="Setup progress">
        {steps.map((item, index) => {
          const completed = index < activeIndex
            || (item.id === 'courses' && savedCourses.length > 0)
            || (item.id === 'clubs' && averages.length > 0);
          return (
            <React.Fragment key={item.id}>
              {index > 0 && <span className={`setup-progress__line${completed || index <= activeIndex ? ' is-complete' : ''}`} />}
              <div className={`setup-progress__step${item.id === step ? ' is-active' : ''}${completed ? ' is-complete' : ''}`}>
                <span>{completed ? <Check aria-hidden="true" /> : index + 1}</span>
                <strong>{item.label}</strong>
              </div>
            </React.Fragment>
          );
        })}
      </nav>

      {step === 'courses' && (
        <main className="setup-stage setup-stage--courses">
          <section className="setup-stage__intro">
            <div className="setup-stage__icon"><MapPin aria-hidden="true" /></div>
            <p className="setup-stage__eyebrow">Step 1 of 3</p>
            <h1>Where do you play?</h1>
            <p>Course and hole details give your caddie the layout, par, and playing context behind every recommendation. Save at least one course to get started.</p>
          </section>
          <div className="setup-stage__control"><CourseSearchPanel /></div>
          <footer className="setup-actions">
            <span>{savedCourses.length === 0 ? 'Save one course to continue' : `${savedCourses.length} course${savedCourses.length === 1 ? '' : 's'} saved`}</span>
            <Button label="Continue to your bag" icon="pi pi-arrow-right" iconPos="right" disabled={savedCourses.length === 0} onClick={() => setStep('clubs')} />
          </footer>
        </main>
      )}

      {step === 'clubs' && (
        <main className="setup-stage setup-stage--clubs">
          <section className="setup-stage__intro">
            <div className="setup-stage__icon"><Gauge aria-hidden="true" /></div>
            <p className="setup-stage__eyebrow">Step 2 of 3</p>
            <h1>Teach your caddie your distances</h1>
            <p>Your carry numbers help CaddieSight recommend the club you actually hit to the target, not a generic yardage chart. Import launch-monitor data or enter your clubs manually.</p>
          </section>
          <div className="setup-club-options">
            <section className="setup-option">
              <Upload aria-hidden="true" />
              <div><h2>Import launch-monitor data</h2><p>Upload a CSV or Excel export to add your full bag and measured shot patterns at once.</p></div>
              <Button label="Import file" icon="pi pi-upload" severity="secondary" outlined loading={importing} onClick={() => fileInput.current?.click()} />
              <input ref={fileInput} type="file" accept=".csv,text/csv,.xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" hidden onChange={handleImport} />
            </section>
            <div className="setup-option-divider"><span>or add a club</span></div>
            <section className="setup-manual-club">
              <div className="setup-field">
                <label htmlFor="setup-club-type">Club</label>
                <Dropdown inputId="setup-club-type" value={club.clubType} options={GOLF_CLUBS} filter placeholder="Select a club" onChange={(event) => setClub((current) => ({ ...current, clubType: event.value }))} />
              </div>
              <div className="setup-field">
                <label htmlFor="setup-club-brand">Brand <small>optional</small></label>
                <InputText id="setup-club-brand" value={club.clubBrand ?? ''} placeholder="e.g. Callaway" onChange={(event) => setClub((current) => ({ ...current, clubBrand: event.target.value }))} />
              </div>
              <div className="setup-field">
                <label htmlFor="setup-club-carry">Carry ({distanceUnit})</label>
                <InputNumber inputId="setup-club-carry" value={club.carryDistance} min={0} maxFractionDigits={0} placeholder="e.g. 165" onValueChange={(event) => setClub((current) => ({ ...current, carryDistance: event.value ?? 0 }))} />
              </div>
              <div className="setup-field">
                <label htmlFor="setup-club-total">Total ({distanceUnit}) <small>optional</small></label>
                <InputNumber inputId="setup-club-total" value={club.totalDistance || null} min={0} maxFractionDigits={0} placeholder="Defaults to carry" onValueChange={(event) => setClub((current) => ({ ...current, totalDistance: event.value ?? 0 }))} />
              </div>
              <Button label="Add club" icon="pi pi-plus" loading={saving} onClick={handleAddClub} />
            </section>
          </div>
          {averages.length > 0 && <div className="setup-saved-clubs"><Check aria-hidden="true" /><strong>{averages.length} club{averages.length === 1 ? '' : 's'} ready</strong><span>You can add more now or update your bag later in Profile.</span></div>}
          <footer className="setup-actions">
            <Button label="Back" icon="pi pi-arrow-left" severity="secondary" text onClick={() => setStep('courses')} />
            <Button label="Finish setup" icon="pi pi-arrow-right" iconPos="right" disabled={averages.length === 0} onClick={() => setStep('ready')} />
          </footer>
        </main>
      )}

      {step === 'ready' && (
        <main className="setup-stage setup-stage--ready">
          <section className="setup-stage__intro">
            <div className="setup-stage__icon"><Flag aria-hidden="true" /></div>
            <p className="setup-stage__eyebrow">Setup complete</p>
            <h1>Your caddie is ready</h1>
            <p>CaddieSight now knows where you play and how far you hit your clubs. That context makes every on-course recommendation more personal and more precise.</p>
          </section>
          <div className="setup-summary">
            <div><MapPin aria-hidden="true" /><span><strong>{savedCourses.length}</strong> saved course{savedCourses.length === 1 ? '' : 's'}</span></div>
            <div><Gauge aria-hidden="true" /><span><strong>{averages.length}</strong> club{averages.length === 1 ? '' : 's'} with data</span></div>
          </div>
          <Button label="Go to Play" icon="pi pi-flag" size="large" onClick={() => navigate('/play', { replace: true })} />
        </main>
      )}
    </div>
  );
};

export default SetupPage;